/**
 * useAudioMetadata Hook
 *
 * Single Responsibility: Extract audio metadata from blobs/files
 * Interface Segregation: Minimal API - one function, clear return type
 */

import { useCallback, useState } from 'react';

import { type AudioMetadata, type AudioMetadataResult, getAudioMetadata } from '../utils/audioFormats';

export interface UseAudioMetadataReturn extends UseAudioMetadataState {
  /** Extract metadata from audio blob */
  extract: (blob: Blob) => Promise<AudioMetadataResult | null>;
  /** Reset state */
  reset: () => void;
}

export interface UseAudioMetadataState {
  /** Error message if extraction failed */
  error: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Extracted metadata */
  metadata: AudioMetadata | null;
}

/**
 * Hook for extracting audio metadata from blobs
 *
 * @example
 * const { extract, metadata, isLoading, error } = useAudioMetadata();
 *
 * const handleFile = async (file: File) => {
 *   const result = await extract(file);
 *   if (result) {
 *     console.log('Duration:', result.metadata.duration);
 *   }
 * };
 */
export function useAudioMetadata(): UseAudioMetadataReturn {
  const [state, setState] = useState<UseAudioMetadataState>({
    error: null,
    metadata: null,
    isLoading: false,
  });

  const extract = useCallback(async (blob: Blob): Promise<AudioMetadataResult | null> => {
    setState({ error: null, metadata: null, isLoading: true });

    try {
      const result = await getAudioMetadata(blob);

      setState({
        error: null,
        isLoading: false,
        metadata: result.metadata,
      });

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to extract audio metadata';

      setState({
        metadata: null,
        isLoading: false,
        error: errorMessage,
      });

      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ error: null, metadata: null, isLoading: false });
  }, []);

  return {
    ...state,
    reset,
    extract,
  };
}
